/**
 * Storage service — uploads and downloads backed by Firebase Storage.
 *
 * The mentor area uses this to attach lesson notes/materials to a class. Files
 * live under `class-notes/{classId}/...`; the resulting download URL + metadata
 * is then recorded on the class document (see classesService.addMaterial).
 */
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

import { auth, storage } from './config';

/** Strip characters that don't belong in a storage object name. */
function safeName(name = 'file') {
  return name.replace(/[^\w.-]+/g, '_').slice(0, 120) || 'file';
}

/**
 * The signed-in uploader's uid, embedded in the storage path so the security
 * rules can scope write/delete to the person who created the object (Storage
 * rules can't read Firestore to verify class membership). Throws if there's no
 * session — every upload path here is behind auth, so this should never fire.
 */
function requireUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in.');
  return uid;
}

export const storageService = {
  /**
   * Upload a File/Blob to `path`, reporting integer progress (0–100) via
   * `onProgress`. Resolves to `{ url, path }`.
   */
  upload(path, file, onProgress) {
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(ref(storage, path), file);
      task.on(
        'state_changed',
        (snap) => {
          const pct = snap.totalBytes
            ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
            : 0;
          onProgress?.(pct);
        },
        reject,
        async () => {
          try {
            resolve({ url: await getDownloadURL(task.snapshot.ref), path });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  },

  /**
   * Upload a lesson note for a class and return the metadata object to persist
   * on the class document.
   */
  async uploadClassNote(classId, file, onProgress) {
    const path = `class-notes/${classId}/${requireUid()}/${Date.now()}-${safeName(file.name)}`;
    const { url } = await this.upload(path, file, onProgress);
    return {
      name: file.name,
      url,
      path,
      size: file.size,
      type: file.type || '',
      uploadedAt: Date.now(),
    };
  },

  /**
   * Upload a session-log attachment for a class and return the metadata object
   * to persist in the log's `attachments[]` array.
   */
  async uploadSessionAttachment(classId, file, onProgress) {
    const path = `session-logs/${classId}/${requireUid()}/${Date.now()}-${safeName(file.name)}`;
    const { url } = await this.upload(path, file, onProgress);
    return {
      name: file.name,
      url,
      path,
      size: file.size,
      type: file.type || '',
      uploadedAt: Date.now(),
    };
  },

  /** Permanently delete an uploaded object by its storage path. */
  remove(path) {
    return deleteObject(ref(storage, path));
  },
};

export default storageService;
